// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React from 'react'
import { EnvironmentOutlined, UserOutlined, CameraOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Row, Col, AutoComplete, Input } from 'antd'
import { RadioButtonGroup } from '../../components/camera-directory/RadioButtonGroup'
import { SearchType } from 'models/directory'
import { RootState, Dispatch } from 'store'
import { connect } from 'react-redux'

function mapState (state: RootState) {
  return {
    searchValue: state.directory.search.value,
    searchType: state.directory.search.type,
    cities: state.city.cityList
  }
}

function mapDispatch (dispatch: Dispatch) {
  return {
    setSearch: dispatch.directory.setSearch,
    searchDevices: dispatch.directory.searchDevices
  }
}
export type IDirectorySearchProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>

export interface IDirectorySearchState {
  options: { label: string; value: string }[]
  value: string
}

export class DirectorySearch extends React.Component<IDirectorySearchProps, IDirectorySearchState> {
  static defaultProps = {
    cities: []
  }

  constructor (props) {
    super(props)
    this.state = {
      options: [],
      value: null
    }
  }

  componentWillUnmount () {
    this.setState({
      options: [],
      value: null
    })
  }

  private generateSearchOptions = (value: string) => {
    const { cities } = this.props
    let options
    if (value.trim() === '') {
      options = []
    }
    options = cities
      .filter(d => d.name.toLowerCase().indexOf(value.toLowerCase()) > -1)
      .map(d => ({
        label: d.name,
        value: d.name
      }))
    this.setState({
      options
    })
  }

  private handleSearchTypeChange = (searchType: string) => {
    if (!searchType) {
      return
    }
    this.props.setSearch({
      type: Number(searchType)
    })
  }

  private handleSearch = (value: string) => {
    this.props.searchDevices({
      type: this.props.searchType,
      value: value
    })
  }

  private handleOptionSelect = (value: string) => {
    this.setState({
      value
    })
    this.props.searchDevices({
      type: this.props.searchType,
      value: value
    })
  }

  private handleCitySearch = (value, e) => {
    e.preventDefault()
  }

  private handleCityChange = e => {
    e.preventDefault()
  }

  private handleClear = () => {
    this.setState({
      value: null
    })
    this.props.searchDevices({
      type: this.props.searchType,
      value: null
    })
  }

  private handleChange = e => {
    this.setState({
      value: e.target.value
    })
  }

  private handleAutoCompleteValueChange = (value: string) => {
    this.setState({
      value
    })
  }

  private renderSearchInputBox = () => {
    const { options, value } = this.state
    const { searchType } = this.props
    const inputProps = {
      placeholder: `Input the camera's current city`,
      prefix: <SearchOutlined />,
      enterButton: 'Search',
      onSearch: this.handleSearch,
      value,
      onChange: this.handleChange
    }
    if (searchType === SearchType.CityID) {
      return (
        <AutoComplete
          className='directory-search-auto-complete'
          options={options}
          onSearch={this.generateSearchOptions}
          onSelect={this.handleOptionSelect}
          value={value}
          onChange={this.handleAutoCompleteValueChange}
        >
          <Input.Search {...inputProps} onSearch={this.handleCitySearch} onChange={this.handleCityChange} />
        </AutoComplete>
      )
    }
    if (searchType === SearchType.DriverID) {
      inputProps.placeholder = 'Input driver id'
    } else if (searchType === SearchType.DeviceID) {
      inputProps.placeholder = 'Input camera id'
    }
    return <Input.Search {...inputProps} />
  }

  render () {
    const { searchType } = this.props
    return (
      <div className='directory-search'>
        <Row gutter={16} align='middle' className='search-type'>
          <Col>Search by:</Col>
          <Col>
            <RadioButtonGroup activedKey={searchType.toString()} onChange={this.handleSearchTypeChange}>
              <Button key={SearchType.CityID.toString()}>
                <EnvironmentOutlined />
                City
              </Button>
              <Button key={SearchType.DriverID.toString()}>
                <UserOutlined />
                Driver ID
              </Button>
              <Button key={SearchType.DeviceID.toString()}>
                <CameraOutlined />
                Camera ID
              </Button>
            </RadioButtonGroup>
          </Col>
        </Row>
        <Row className='search-box'>
          <Col flex={1} style={{ marginRight: 8 }}>
            {this.renderSearchInputBox()}
          </Col>
          <Col>
            <Button onClick={this.handleClear}>Clear</Button>
          </Col>
        </Row>
      </div>
    )
  }
}
export default connect(mapState, mapDispatch)(DirectorySearch)
